"use client";

const RoommateCard = ({
  coverPhoto,
  title,
  price,
  details,
  genderPreference,
  ageRange,
  moveInDate,
  location,
  className = "",
  imageAlt = "",
  badgeClassName = "",
  titleClassName = "",
  priceClassName = "",
  locationClassName = "",
  onClick,
  ...props
}) => {
  // Parse specific details if they're in string format
  const parseDetails = (detailsArray) => {
    if (!detailsArray) return {};

    if (Array.isArray(detailsArray)) {
      const detailsMap = {};
      detailsArray.forEach((detail) => {
        if (typeof detail === "string") {
          const [key, value] = detail.split(":");
          if (key && value) {
            detailsMap[key] = value;
          }
        } else if (detail && detail.key && detail.value) {
          detailsMap[detail.key] = detail.value;
        }
      });
      return detailsMap;
    }

    return detailsArray;
  };

  const detailsMap = parseDetails(details);
  const displayGenderPreference =
    genderPreference || detailsMap.genderPreference || "Any";
  const displayAgeRange = ageRange || detailsMap.ageRange || "Any";
  const displayMoveInDate = moveInDate || detailsMap.moveInDate || "Flexible";

  // Format move-in date for display
  const formatMoveInDate = (date) => {
    if (!date || date === "Flexible") return "Flexible";
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return date;
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick({
        title,
        price,
        image: coverPhoto,
        images: [coverPhoto],
        description: props.description || "",
        category: "Roommate",
        categoryId: 30, // Roommate category ID
        genderPreference: displayGenderPreference,
        ageRange: displayAgeRange,
        moveInDate: displayMoveInDate,
        leaseDuration:
          props.leaseDuration || detailsMap.leaseDuration || "Flexible",
        lifestyle: props.lifestyle || detailsMap.lifestyle || "Flexible",
        location,
        distance: props.distance || "1.2 km away",
        story: props.story || "",
        additionalDetails: props.additionalDetails || "",
        seeker: props.seeker || {
          name: "Roommate Seeker",
          verified: true,
          address: location,
        },
      });
    }
  };

  return (
    <div
      className={`col-span-1 flex justify-center items-center w-[244px] h-[280px] rounded-xl bg-white shadow-sm border cursor-pointer hover:shadow-md transition-shadow ${className}`}
      onClick={handleClick}
    >
      <div className="w-[244px] h-[280px] flex flex-col">
        <div className="relative w-[244px] h-[180px] border-b">
          <img
            src={
              coverPhoto ||
              "/placeholder.svg?height=400&width=600&query=person looking for roommate"
            }
            alt={imageAlt || title}
            className="w-full h-full object-cover rounded-t-xl"
          />
          <div className="absolute top-3 right-3 bg-white/90 text-black font-medium py-1 px-2 rounded-lg">
            {displayGenderPreference === "no-preference"
              ? "Any"
              : displayGenderPreference}
          </div>
        </div>
        <div className="p-3 h-[100px] flex flex-col">
          <h3
            className={`font-medium text-base mb-1 line-clamp-1 text-gray-900 ${titleClassName}`}
          >
            {title}
          </h3>
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
            <div className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{formatMoveInDate(displayMoveInDate)}</span>
            </div>
            <div className="flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              <span>{displayAgeRange}</span>
            </div>
          </div>
          <div className="mt-auto flex items-center justify-between">
            <div
              className={`font-bold text-gray-900 text-lg ${priceClassName}`}
            >
              <span className="text-sm align-top">$</span>
              {price}
              <span className="text-sm text-gray-500">/mo</span>
            </div>
            <div
              className={`flex items-center text-xs text-gray-500 ${locationClassName}`}
            >
              {location}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoommateCard;
